/**
 * DeviceDetail — device drill-down rendered as an edge Sheet (Radix Dialog).
 *
 * Design (sec 7): detail opens as an edge Sheet over the list. Live tools
 * (reboot) live here and are gated by RoleGate: read_only never SEES the reboot
 * control; a site_operator who lacks it at this scope sees it disabled with a
 * reason. Reboot is destructive, so it also demonstrates the offline-block rule
 * (blocked, never queued) via navigator.onLine, and it ASKS FIRST: the button opens an
 * in-page confirmation naming the device and its outage (RebootConfirmPanel, UI review
 * 2026-10-17); gateways additionally need a "someone is on site" acknowledgement.
 *
 * Closing the Sheet navigates back to the list route (the URL owns the state).
 */
import { formatPortRefs, formatSwitchPort } from "@/lib/switchPorts";
import { lldpAge } from "@/lib/lldpAge";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { ConfigDriftPanel } from "@/components/device/ConfigDriftPanel";
import { DeviceDiagnostics } from "@/components/device/DeviceDiagnostics";
import {
  useDevice,
  useRebootDevice,
  useDeviceConfigActions,
  usePreviewConfigAction,
  useApplyConfigAction,
  useAssignDevices,
  useDeviceTags,
  useSetDeviceTags,
  useRenameDevice,
  useDeviceFirmware,
  useUpgradeFirmware,
  useDeviceConnectivity,
  useDeviceConfigStatus,
  useSetConfigExcluded,
  useDeviceSsids,
  useDeviceGateway,
} from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { ApplyGroupSsids } from "@/components/device/ApplyGroupSsids";
import { portTableHref } from "@/lib/portTable";
import { RoleGate } from "@/components/auth/RoleGate";
import { DeviceStatusPill } from "@/components/ui/StatusPill";
import {
  ConfigStatusPill,
  ConfigIncompatibleBadge,
  configReasonText,
} from "@/components/ui/ConfigStatusPill";
import { Sparkline } from "@/components/data/Sparkline";
import { ClientsCount } from "@/components/device/ClientsCount";
import { SwitchPortsSection } from "@/components/site/SwitchPortsSection";
import { SonicfiAdoptCard } from "@/components/device/SonicfiAdoptCard";
import { isSonicfiSwitch } from "@/lib/sonicfiAdopt";
import { SwitchPendingVlansFlag } from "@/components/site/SiteVlanRollout";
import { LivePortTable } from "@/components/site/LivePortTable";
import { ApPortsSection } from "@/components/site/ApPortsSection";
import { DeviceRadiosSection } from "@/components/device/DeviceRadiosSection";
import { ApBaselineCard } from "@/components/device/ApBaselineCard";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { DeviceTerminal } from "@/components/device/DeviceTerminal";
import { DiagnoseCard } from "@/components/ai/DiagnoseCard";
import { DeviceLocationPhotos } from "@/components/device/DeviceLocationPhotos";
import { ReassertGatewayButton } from "@/components/gateway/ReassertManaged";
import { StateReportingNotice } from "@/components/gateway/EnableStateReporting";
import { TERMINAL_MIN_ROLE, supportsTerminal } from "@/lib/terminal/policy";
import { relTime, fmtAbsolute, parseApiTime } from "@/lib/format";
import {
  REBOOT_FROM_MERIDIAN_AVAILABLE,
  REBOOT_UNAVAILABLE_REASON,
  canConfirmReboot,
  rebootWarning,
} from "@/lib/rebootConfirm";
import { moveResultLines } from "@/lib/deviceMove";
import { CustomerDefaultOptIn } from "@/components/device/CustomerDefaultOptIn";
import type {
  ConfigCompat,
  Device,
  DeviceAddress,
  DeviceConfigAction,
  DeviceUplink,
  SwitchPort,
} from "@/types/domain";
import { useState, useSyncExternalStore, type ReactNode } from "react";

type DeviceTab = "overview" | "diagnostics";

/** Underline tab trigger: 44px touch height, brand underline on the active view, visible focus. */
const TAB_TRIGGER =
  "-mb-px min-h-touch border-b-2 border-transparent px-3 text-sm font-medium text-ink-muted " +
  "hover:text-ink data-[state=active]:border-brand data-[state=active]:text-ink " +
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand";

/** Subscribe to browser online/offline for the destructive-action block. */
function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("online", cb);
      window.addEventListener("offline", cb);
      return () => {
        window.removeEventListener("online", cb);
        window.removeEventListener("offline", cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

export default function DeviceDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serial, orgId, siteId } = useParams();
  const { data: device, isLoading, error } = useDevice(serial);
  const reboot = useRebootDevice();
  const online = useOnline();
  // Web CLI sheet (msp_admin; the server re-checks role + tenancy on open AND on the websocket).
  const [terminalOpen, setTerminalOpen] = useState(false);
  // Reboot asks first: true while the in-page confirmation panel is showing.
  const [rebootAsk, setRebootAsk] = useState(false);
  // Sheet view: the device overview, or the Diagnostics tab (OP #1663).
  const [tab, setTab] = useState<DeviceTab>("overview");

  // Close returns to the list the Sheet was opened over -- the PARENT path (…/switches,
  // …/access-points or …/devices, org or site scope) WITH its query string, so the list's
  // search and filters survive opening a device (owner 2026-09-14). The old code always went
  // to …/devices without the query, dropping both the section and every filter.
  const close = () => {
    const parent = location.pathname.replace(/\/[^/]+\/?$/, "");
    const fallback = siteId ? `/o/${orgId}/s/${siteId}/devices` : `/o/${orgId}/devices`;
    navigate((parent && parent !== location.pathname ? parent : fallback) + location.search);
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        {/* Edge sheet: full-width on tablet, right-anchored panel on desktop. */}
        <Dialog.Content
          className="fixed inset-y-0 right-0 z-sheet flex w-full max-w-md flex-col bg-overlay shadow-sheet outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <Dialog.Title className="text-lg font-semibold text-ink">
              {device?.name ?? serial}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label="Close">
                ✕
              </Button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {error && <ErrorBanner error={error} />}
            {isLoading && <div className="text-sm text-ink-muted">Loading device…</div>}
            {device && (
              <Tabs.Root value={tab} onValueChange={(v) => setTab(v as DeviceTab)}>
              {/* Overview | Diagnostics. Diagnostics is hidden from read_only: every entry
                  needs site_operator or above (the server enforces it per entry). A
                  site_operator sees only the controller-state reads; gateway shell / switch
                  CLI entries are org_admin+ (option A, 2026-10-09). */}
              <Tabs.List aria-label="Device views" className="mb-4 flex gap-1 border-b border-line">
                <Tabs.Trigger value="overview" className={TAB_TRIGGER}>Overview</Tabs.Trigger>
                <RoleGate min="site_operator" mode="hide">
                  <Tabs.Trigger value="diagnostics" className={TAB_TRIGGER}>Diagnostics</Tabs.Trigger>
                </RoleGate>
              </Tabs.List>
              <Tabs.Content value="overview" className="outline-none">
              <div className="flex flex-col gap-5">
                {/* Identity — the fields you need to pin down exactly which box this is. */}
                <section>
                  <SectionTitle>Identity</SectionTitle>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2">
                      <Field label="Name">
                        <EditableName serial={device.serial} name={device.name} />
                      </Field>
                    </div>
                    <Field label="Status">
                      <DeviceStatusPill status={device.status} />
                    </Field>
                    <Field label="Reachable">{device.online ? "Yes" : "No"}</Field>
                    <Field label="Serial">
                      <Mono>{device.serial}</Mono>
                    </Field>
                    <Field label="Model">{device.model ?? "—"}</Field>
                    <Field label="MAC">
                      <Mono>{device.mac ?? "—"}</Mono>
                    </Field>
                    {/* Public = where owgw saw it connect from (the site's NAT egress);
                        Private = the box's own LAN / management address. */}
                    <Field label="Public IP">
                      <Mono>{device.ip ?? "—"}</Mono>
                    </Field>
                    <Field label="Private IP">
                      <Mono>{device.private_ip ?? "—"}</Mono>
                    </Field>
                    <InterfaceAddresses device={device} />
                    <Field label="Firmware">{device.firmware ?? "—"}</Field>
                    <Field label="Clients">
                      {/* Connected now; "—" + last-known line when offline. */}
                      <ClientsCount device={device} />
                    </Field>
                    {/* Contacted at, not "Last seen": the field answers when the DEVICE last
                        spoke to us. "never" is a real and meaningful value here. */}
                    <Field label="Contacted at">
                      <span
                        className={device.last_seen ? undefined : "text-ink-subtle"}
                        title={
                          device.last_seen
                            ? fmtAbsolute(device.last_seen)
                            : "This device has never contacted the controller."
                        }
                      >
                        {relTime(device.last_seen)}
                      </span>
                    </Field>
                    {/* Config state sits at the same level as serial and firmware because it
                        is the same KIND of fact: a permanent property of this box that an
                        operator needs without clicking. It is also the competitive claim
                        ("you can trust what the controller tells you") made visible. */}
                    <div className="col-span-2">
                      <Field label="Config">
                        <ConfigSummary serial={device.serial} />
                      </Field>
                    </div>
                    {/* The state-document clock, shown ONLY when it lags the control channel.
                        A device can hold an open session and have stopped producing state —
                        that gap is how a dead-but-connected device hides, and there is no
                        reason to spend a row on it when the two clocks agree. */}
                    <StateReportGap device={device} />
                  </dl>
                </section>

                {/* Location & photos: the installer's note and up to 3 photos of where this
                    box physically is (owner 2026-09-14: the AP nobody could find "behind the
                    basement TV"). Right under Identity: it is the next thing someone on site needs. */}
                <DeviceLocationPhotos serial={device.serial} description={device.description ?? null} />

                {/* Connectivity over time — "has this been stable?", which the status pill
                    above cannot answer. */}
                <section>
                  <SectionTitle>Last 24 hours</SectionTitle>
                  <ConnectivityTimeline serial={device.serial} />
                </section>

                {/* Connectivity — the uplink port + negotiated speed, and (from LLDP)
                    exactly which switch port this device is plugged into. */}
                <section>
                  <SectionTitle>Connectivity</SectionTitle>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="Uplink port">
                      {/* One-based for switches (uCentral names ports from 0; lib/switchPorts). */}
                      <Mono>{device.uplink?.port ? formatSwitchPort(device.uplink.port) : "—"}</Mono>
                    </Field>
                    <Field label="Link speed">{fmtSpeed(device.uplink)}</Field>
                    <Field label="Connected to (LLDP)">
                      {device.lldp?.system_name ? (
                        <span className={lldpAge(device.lldp, device.online, Date.now()).stale ? "text-ink-muted" : undefined}>
                          <Mono>{device.lldp.system_name}</Mono>
                          {device.lldp.port_id ? (
                            <>
                              {" · port "}
                              <Mono>{formatPortRefs(device.lldp.port_id)}</Mono>
                            </>
                          ) : null}
                          {/* Last-known snapshot: its age, "last seen" when offline (OP#1857). */}
                          <span className="block text-xs text-ink-subtle" data-testid="detail-lldp-age">
                            {lldpAge(device.lldp, device.online, Date.now()).label}
                          </span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </Field>
                    <Field label="Neighbor VLAN">{device.lldp?.vlan ?? "—"}</Field>
                    <Field label="Neighbor mgmt IP">
                      <Mono>{device.lldp?.mgmt_ip ?? "—"}</Mono>
                    </Field>
                  </dl>
                </section>

                {/* Placement — which site this device belongs to, and move it. */}
                <PlacementSection
                  serial={device.serial}
                  currentSiteId={device.site_id}
                  siteName={device.site_name ?? null}
                  orgId={device.org_id}
                />

                {/* Tags — the groups this AP belongs to (SSIDs can target a tag). */}
                <TagsSection serial={device.serial} />

                {/* Configuration — operator-applied, owgw-pushed config actions.
                    Each one previews a diff, then applies with an audit trail; the
                    AP is never touched by hand. */}
                <ConfigurationSection serial={device.serial} kind={device.kind} />

                {/* Root-cause — read-only AI diagnosis from the device's LIVE owgw facts + the KB.
                    Advisory only (no RoleGate, no confirm): it changes nothing. */}
                <DiagnoseCard serial={device.serial} />

                {/* Ethernet ports — wall-plate APs. The port PROFILE is the base layer and a
                    per-(device, port) OVERRIDE is the exception on top of it, so one port of
                    one AP can deviate without editing the profile every other AP shares. */}
                {device.kind === "access_point" && (
                  <section>
                    <SectionTitle>Ports</SectionTitle>
                    <ApPortsSection serial={device.serial} siteId={device.site_id} />
                  </section>
                )}

                {/* Radios — per-band channel / width / TX power: inherits the site's Radio
                    Settings unless this AP overrides a field (OP#1760). APs only. */}
                {device.kind === "access_point" && (
                  <section>
                    <SectionTitle>Radios</SectionTitle>
                    <DeviceRadiosSection serial={device.serial} />
                  </section>
                )}

                {/* AP baseline (OP#1775) — factory routed LAN -> bridged, 6 GHz off Open/WPA2
                    SSIDs. Applied ONLY from here, one AP at a time (never by SSID/radio pushes). */}
                {device.kind === "access_point" && (
                  <section>
                    <SectionTitle>AP baseline</SectionTitle>
                    <ApBaselineCard serial={device.serial} />
                  </section>
                )}

                {/* SSIDs — exactly what this AP is broadcasting (live config + client counts),
                    the Meraki/UniFi AP-pane view. APs only. */}
                {device.kind === "access_point" && <SsidsSection serial={device.serial} />}

                {/* Gateway — what a whitebox uCentral gateway is doing on the wire (WAN/LAN roles,
                    VLANs, DHCP scopes, NAT, captive), read straight from its live config. */}
                {/* Ports, for a switch. High in the sheet on purpose: for a switch this IS
                    the device -- "what is plugged in where, and what is that port called" is
                    the question an operator opens a switch to answer, and it used to be
                    unanswerable from this screen. */}
                {device.kind === "switch" && (
                  <section>
                    <SectionTitle>Ports</SectionTitle>
                    {/* Skipped while offline during a site VLAN rollout: flag + re-plan link. */}
                    <SwitchPendingVlansFlag serial={device.serial} />
                    <SwitchPortsSection
                      serial={device.serial}
                      uplinkPort={device.uplink?.port ?? null}
                    />
                    {/* SonicFi/CyberTan RSW (OP#1838): the config of record built from the running
                        switch, read-only. These switches boot their factory config, so hand VLAN
                        edits are lost at restart until Meridian saves one (OP#1820). */}
                    {isSonicfiSwitch(device.model, device.firmware) && (
                      <div className="mt-3">
                        <SonicfiAdoptCard serial={device.serial} />
                      </div>
                    )}
                    {/* The same live port table the AP sheet leads with (one component, one
                        response shape): current VLANs per port (OP#1639), learned MACs per
                        VLAN, LLDP and traffic, read across instead of port by port. Collapsed:
                        the faceplate above stays the primary control for a switch. */}
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs font-medium text-ink-muted">
                        Port table — link, VLANs, MACs, LLDP, traffic
                      </summary>
                      <div className="mt-2">
                        <LivePortTable serial={device.serial} />
                      </div>
                    </details>
                  </section>
                )}

                {device.kind === "gateway" && <GatewaySection serial={device.serial} />}

                {/* Firmware — running version, the managed baseline for this model, and a
                    one-click upgrade (owgw-driven) with live status. Any uCentral device owgw
                    can `upgrade`: APs and whitebox gateways (QOTOM/x64_vm). Switches join once
                    OLS units actually connect (they are SNMP/ecCLI-managed until the TIP-signed
                    owgw cert lands, and an owgw upgrade of an SNMP-only switch would just hang
                    as a PENDING intent). */}
                {(device.kind === "access_point" || device.kind === "gateway") && (
                  <FirmwareSection serial={device.serial} />
                )}

                {/* Ports — switches only: per-port link state, VLAN, PoE, and what
                    LLDP says is plugged into each port. */}
                {device.ports && device.ports.length > 0 && (
                  <section>
                    <SectionTitle>Ports ({device.ports.length})</SectionTitle>
                    <PortsTable ports={device.ports} />
                  </section>
                )}
              </div>
              </Tabs.Content>

              {/* Diagnostics — named, read-only checks run live on the device (audited,
                  redacted) + the support bundle. Mounted only while selected, so opening a
                  device never fetches the catalogue or touches the box. */}
              <Tabs.Content value="diagnostics" className="outline-none">
                <DeviceDiagnostics serial={device.serial} kind={device.kind ?? ""} />
              </Tabs.Content>
              </Tabs.Root>
            )}
          </div>

          {/* Live tools — gated. read_only never sees reboot (hide); a role that
              could-but-can't at this scope sees it disabled with a reason. */}
          <div className="border-t border-line px-4 py-3">
            <RoleGate
              min="site_operator"
              mode="hide"
              fallback={
                <p className="text-xs text-ink-subtle">
                  You have read-only access to this device.
                </p>
              }
            >
              {/* In-page reboot confirmation (UI review 2026-10-17): the first click only OPENS
                  this panel; nothing is posted until the operator confirms here. */}
              {REBOOT_FROM_MERIDIAN_AVAILABLE && rebootAsk && device && serial && (
                <RebootConfirmPanel
                  device={device}
                  pending={reboot.isPending}
                  onCancel={() => setRebootAsk(false)}
                  onConfirm={() =>
                    reboot.mutate(serial, { onSettled: () => setRebootAsk(false) })
                  }
                />
              )}
              <div className="flex items-center gap-2">
                {/* OP#1929 (interim): the reboot API never reached a device (it recorded a fake
                    SENT), so the button is DISABLED for every device kind, with the reason as
                    its tooltip (on a wrapper: a disabled button fires no hover events) and as
                    visible text. The confirm panel and its per-kind warnings (lib/rebootConfirm)
                    stay for when the real owgw reboot lands. */}
                <span
                  title={REBOOT_FROM_MERIDIAN_AVAILABLE ? undefined : REBOOT_UNAVAILABLE_REASON}
                  data-testid="reboot-tooltip"
                >
                  <Button
                    variant="danger"
                    disabled={!REBOOT_FROM_MERIDIAN_AVAILABLE || !online || reboot.isPending || !device || rebootAsk}
                    title={
                      !REBOOT_FROM_MERIDIAN_AVAILABLE
                        ? REBOOT_UNAVAILABLE_REASON
                        : !online
                          ? "Reboot is blocked while offline"
                          : undefined
                    }
                    aria-describedby={!REBOOT_FROM_MERIDIAN_AVAILABLE ? "reboot-unavailable-reason" : undefined}
                    onClick={() => setRebootAsk(true)}
                  >
                    {reboot.isPending ? "Rebooting…" : "Reboot"}
                  </Button>
                </span>
                {!REBOOT_FROM_MERIDIAN_AVAILABLE && (
                  <span id="reboot-unavailable-reason" className="text-xs text-ink-muted">
                    {REBOOT_UNAVAILABLE_REASON}
                  </span>
                )}
                {REBOOT_FROM_MERIDIAN_AVAILABLE && !online && (
                  <span className="text-xs text-warn">
                    Offline — destructive actions are blocked, not queued.
                  </span>
                )}
                {reboot.isError && (
                  <span className="text-xs text-critical">
                    {(reboot.error as Error)?.message || "Reboot failed."}
                  </span>
                )}
                {/* Terminal: a root shell over owgw RTTY. Left enabled while the controller
                    thinks the device is offline -- owgw's view can be fresher than ours, and the
                    sheet explains a refusal (device offline / RTTY unavailable) precisely. */}
                {device && supportsTerminal(device.kind) && (
                  <RoleGate min={TERMINAL_MIN_ROLE} mode="hide">
                    <Button variant="secondary" className="ml-auto" onClick={() => setTerminalOpen(true)}>
                      Terminal
                    </Button>
                  </RoleGate>
                )}
              </div>
            </RoleGate>
          </div>
          {device && supportsTerminal(device.kind) && (
            <DeviceTerminal
              open={terminalOpen}
              onOpenChange={setTerminalOpen}
              device={{ serial: device.serial, name: device.name, kind: device.kind }}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * RebootConfirmPanel -- the in-page "are you sure" for Reboot (UI review 2026-10-17).
 *
 * Names the device and spells out the outage for its kind (lib/rebootConfirm). For a gateway
 * the confirm button stays disabled until "someone is on site" is ticked: a remote soft reboot
 * of a QOTOM/J1900-class gateway can hang, and only a power cycle on site recovers it.
 * role="alertdialog" + aria-labelledby/-describedby so assistive tech announces it, and so the
 * e2e suite can address it without a window.confirm.
 */
function RebootConfirmPanel({
  device,
  pending,
  onCancel,
  onConfirm,
}: {
  device: Device;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const w = rebootWarning(device.kind, device.name, device.serial);
  const [acked, setAcked] = useState(false);
  const toneBox = w.tone === "critical"
    ? "border-critical bg-critical-soft"
    : "border-warn bg-warn-soft";
  return (
    <div
      role="alertdialog"
      aria-labelledby="reboot-confirm-title"
      aria-describedby="reboot-confirm-impact"
      data-testid="reboot-confirm"
      className={`mb-3 rounded border p-3 text-sm ${toneBox}`}
    >
      <p id="reboot-confirm-title" className="font-semibold text-ink">{w.title}</p>
      <p id="reboot-confirm-impact" className="mt-1 text-ink">{w.impact}</p>
      {w.ackLabel && (
        <label className="mt-2 flex items-start gap-2 text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acked}
            onChange={(e) => setAcked(e.target.checked)}
          />
          <span>{w.ackLabel}</span>
        </label>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={pending || !canConfirmReboot(w, acked)}
        >
          {pending ? "Rebooting…" : w.confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * Tags — the groups an AP belongs to. An SSID (or other config) can target a tag so
 * it applies only to the APs carrying it (e.g. a "conf" group for conference-room APs).
 */
/**
 * Config verdict for the identity rail, with the reason spelled out.
 *
 * The list column has room for a chip; this has room for the sentence, and the sentence is
 * the part an operator acts on. A verdict of "Not verified" is useless without "the device
 * has not re-reported since the push" versus "the changed settings do not appear in device
 * state" — those are different problems with different next steps.
 */
function ConfigSummary({ serial }: { serial: string }) {
  const { data, isLoading } = useDeviceConfigStatus(serial);
  if (isLoading) return <span className="text-ink-muted">Checking…</span>;

  const latest = data?.latest ?? undefined;
  const reason = configReasonText(latest?.reason ?? (data ? "never_verified" : undefined));

  return (
    <div className="flex flex-col gap-1">
      <ConfigStatusPill
        status={
          data
            ? {
                serial,
                verdict: data.verdict,
                coverage: data.coverage,
                change_scope: data.change_scope,
                reason: latest?.reason ?? "never_verified",
                checked_at: latest?.checked_at ?? null,
                last_checked_at: latest?.last_checked_at ?? null,
                mismatch_count: latest?.mismatches?.length ?? 0,
                // Offline device -> the pill renders neutral "Last confirmed … (device
                // offline)" instead of a live green tick (config_verify.staleness).
                device_online: data.device_online,
                stale: data.stale,
                stale_reason: data.stale_reason,
                last_confirmed_at: data.last_confirmed_at,
                // Excluded from Meridian config: the verdict stays honest, the tooltip
                // explains it is left out of site operations (services/config_compat).
                config_excluded: data.config_compat?.excluded,
                config_excluded_source: data.config_compat?.source,
                config_excluded_label: data.config_compat?.label,
                config_excluded_reason: data.config_compat?.reason,
              }
            : undefined
        }
        className="self-start"
      />
      <ConfigCompatPanel serial={serial} compat={data?.config_compat} />
      {data?.stale && (
        <span className="text-xs text-ink-subtle">
          Device offline — this is its last known config state, not live.
        </span>
      )}
      {reason && <span className="text-xs text-ink-muted">{reason}</span>}
      {latest?.checked_at && (
        <span className="text-xs text-ink-subtle">
          {/* checked_at is when this OUTCOME was first seen, so it reads as "diverged since"
              / "verified since" rather than "we looked at it just now". */}
          {data?.verdict === "diverged" ? "Diverged since" : "Since"}{" "}
          {relTime(latest.checked_at)}
          {latest.last_checked_at && `, last confirmed ${relTime(latest.last_checked_at)}`}
        </span>
      )}
      {/* The actual divergence, named. A "Diverged" chip with no field list forces the
          operator back to a CLI to find out what is wrong, which is most of the work. */}
      {latest?.mismatches && latest.mismatches.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {latest.mismatches.slice(0, 5).map((m) => (
            <li key={m.path} className="font-mono text-xs text-ink-muted">
              <span className="text-critical">{m.path}</span>: expected{" "}
              {JSON.stringify(m.expected)}, found {JSON.stringify(m.actual)}
            </li>
          ))}
          {latest.mismatches.length > 5 && (
            <li className="text-xs text-ink-subtle">
              +{latest.mismatches.length - 5} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Config-compatibility on the device page: the "Config incompatible" badge with its reason, and
 * the operator's escape hatches (org_admin+, audited): "Exclude from Meridian config" for what
 * auto-detection misses, "Include again" to bring it back (which also sends it the current
 * site SSIDs). Jedi Home a47d78ca5d1e (stock EC-12.5.93, 2026-10-11) is the auto-detected case.
 */
function ConfigCompatPanel({ serial, compat }: { serial: string; compat?: ConfigCompat }) {
  const setExcluded = useSetConfigExcluded(serial);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  if (!compat) return null;
  const result = setExcluded.data;
  return (
    <div className="mt-1 flex flex-col gap-1">
      {compat.excluded && (
        <>
          <ConfigIncompatibleBadge
            excluded
            label={compat.label}
            reason={compat.reason}
            className="self-start"
          />
          <span className="text-xs text-ink-muted">
            {compat.label ? `${compat.label}: ` : ""}
            {compat.reason ?? "Excluded from Meridian config."} Site operations skip this device
            (reported as “Not managed”, never as a failure).
          </span>
        </>
      )}
      {!compat.excluded && compat.needs_resync && (
        <span className="text-xs text-ink-muted">
          Site SSIDs changed while this device was excluded — it gets the current SSIDs on the
          next SSID save.
        </span>
      )}
      <RoleGate min="org_admin" mode="hide">
        {compat.excluded && compat.source !== "firmware" ? (
          <Button
            variant="ghost"
            className="self-start"
            disabled={setExcluded.isPending}
            onClick={() => setExcluded.mutate({ exclude: false })}
            title="Manage this device with site operations again and send it the current site SSIDs"
          >
            {setExcluded.isPending ? "Including…" : "Include again"}
          </Button>
        ) : compat.excluded ? (
          <Button
            variant="ghost"
            className="self-start"
            disabled={setExcluded.isPending}
            onClick={() => setExcluded.mutate({ exclude: false })}
            title="Override the firmware check (only after the firmware can apply Meridian config)"
          >
            {setExcluded.isPending ? "Including…" : "Include anyway"}
          </Button>
        ) : confirming ? (
          <div className="flex flex-col gap-1">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why (shown on the device and in the audit log)"
              className="rounded border border-line bg-surface px-2 py-1 text-xs text-ink outline-none focus:border-brand"
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={setExcluded.isPending}
                onClick={() =>
                  setExcluded.mutate(
                    { exclude: true, note: note.trim() || undefined },
                    { onSuccess: () => setConfirming(false) },
                  )
                }
              >
                {setExcluded.isPending ? "Excluding…" : "Exclude"}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            className="self-start"
            onClick={() => setConfirming(true)}
            title="Stop site operations from pushing to this device (it is reported as Not managed)"
          >
            Exclude from Meridian config
          </Button>
        )}
      </RoleGate>
      {setExcluded.isError && (
        <span className="text-xs text-critical">
          {(setExcluded.error as Error)?.message ?? "The change was not saved"}
        </span>
      )}
      {result?.resync_error && (
        <span className="text-xs text-warn">
          Included, but the current site SSIDs could not be sent yet: {result.resync_error}
        </span>
      )}
      {result?.resync?.summary && (
        <span className="text-xs text-ink-muted">
          Current site SSIDs sent: {result.resync.summary.applied} of {result.resync.summary.total}{" "}
          applied{result.resync.summary.failed ? `, ${result.resync.summary.failed} failed` : ""}.
        </span>
      )}
    </div>
  );
}

/**
 * The gap between "last spoke to us" and "last produced state", rendered only when it exists.
 *
 * Measured on the live fleet 2026-09-11: one AP's control channel was 111s old while its last
 * state document was 2h44m old. The device is genuinely connected, so we do not overclaim by
 * calling it offline — but silently showing only the fresh clock is how that device reads as
 * perfectly healthy. The threshold is generous (an hour) so ordinary polling jitter never
 * raises it.
 */
const STATE_GAP_THRESHOLD_SECONDS = 3600;

function StateReportGap({ device }: { device: Device }) {
  // parseApiTime pins naive-UTC server timestamps to UTC (raw new Date() read them as local,
  // skewing the seen-vs-reported gap by the browser's offset). It is null-safe.
  const seen = parseApiTime(device.last_seen);
  const reported = parseApiTime(device.last_state_report_at);

  // No contact at all, or no gap worth reporting -> the field does not exist.
  if (seen == null) return null;
  if (reported != null && (seen - reported) / 1000 < STATE_GAP_THRESHOLD_SECONDS) return null;

  // WHICH SENTENCE IS TRUE DEPENDS ON THE OTHER CLOCK, and the copy used to assume one of
  // them. This component renders whenever the state report lags the control channel by an
  // hour, which includes a device whose control channel is ITSELF three days old — and there
  // it flatly asserted "The control channel is current", i.e. told an operator looking at a
  // device that has been gone since Tuesday that it was connected. The threshold is the same
  // one that decides whether to show the field at all, so "current" here means the same
  // thing it means above: last contact inside the last hour.
  const controlChannelIsCurrent = (Date.now() - seen) / 1000 < STATE_GAP_THRESHOLD_SECONDS;

  return (
    <div className="col-span-2">
      <Field label="Last reported state">
        <div className="flex flex-col gap-0.5">
          <span className="text-warn">
            {reported ? relTime(device.last_state_report_at) : "never"}
          </span>
          <span className="text-xs text-ink-muted">
            {controlChannelIsCurrent ? (
              <>
                The control channel is current, but this device has not produced a state
                report since then — it is connected, not necessarily working.
              </>
            ) : (
              <>
                The control channel is stale too (last contact{" "}
                {relTime(device.last_seen)}), so this is not a reporting gap on a live
                device — nothing has been heard from it at all.
              </>
            )}
          </span>
        </div>
      </Field>
    </div>
  );
}

/**
 * 24-hour connectivity, with the numbers beside the picture.
 *
 * The sparkline alone cannot be read precisely; the counts alone cannot be scanned. Together
 * they answer "has this been stable?" and "how do you know?" at the same time — and the
 * "observed" denominator is what keeps a two-hour-old device from being described as though
 * we watched it all day.
 */
function ConnectivityTimeline({ serial }: { serial: string }) {
  const { data, isLoading } = useDeviceConnectivity(serial, 24);
  if (isLoading) return <div className="text-sm text-ink-muted">Loading timeline…</div>;
  if (!data) return <div className="text-sm text-ink-muted">No connectivity data.</div>;

  return (
    <div className="flex flex-col gap-2">
      <Sparkline buckets={data.buckets} summary={data} className="h-6 w-full" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
        <span className="tabular-nums">{data.up}h connected</span>
        {data.gap > 0 && (
          <span className="tabular-nums text-critical">{data.gap}h without contact</span>
        )}
        {data.unknown > 0 && (
          // Named explicitly rather than left as blank space. "No data" is a statement about
          // OUR coverage, and leaving it unlabelled invites it to be read as an outage.
          <span className="tabular-nums">{data.unknown}h no data</span>
        )}
        <span className="text-ink-subtle">
          {data.observed === 0
            ? "This device has reported nothing in the last 24 hours."
            : data.stable
              ? `Stable across ${data.observed} observed hour${data.observed === 1 ? "" : "s"}.`
              : `${data.gap} outage hour${data.gap === 1 ? "" : "s"} out of ${data.observed} observed.`}
        </span>
      </div>
    </div>
  );
}

function TagsSection({ serial }: { serial: string }) {
  const { data } = useDeviceTags(serial);
  const setTags = useSetDeviceTags(serial);
  const tags = data?.tags ?? [];
  const [input, setInput] = useState("");
  // OP#1778 point 3: a tag change is a DB-only write. When it changes which GROUP-targeted
  // SSIDs belong on this AP, say so and offer to apply them now instead of silently waiting
  // for some unrelated save to reconcile the AP.
  const [ssidChanges, setSsidChanges] = useState<{ add: string[]; remove: string[] } | null>(null);
  const onTagsSaved = (res: { ssid_changes?: { add: string[]; remove: string[] } }) => {
    const c = res.ssid_changes;
    setSsidChanges(c && (c.add.length || c.remove.length) ? c : null);
  };

  const add = () => {
    const t = input.trim();
    if (!t || tags.includes(t)) return;
    setTags.mutate([...tags, t], { onSuccess: onTagsSaved });
    setInput("");
  };
  const remove = (t: string) =>
    setTags.mutate(
      tags.filter((x) => x !== t),
      { onSuccess: onTagsSaved },
    );

  return (
    <section>
      <SectionTitle>Tags (groups)</SectionTitle>
      <div className="flex flex-wrap gap-1.5">
        {tags.length === 0 && <span className="text-xs text-ink-muted">No tags</span>}
        {tags.map((t) => (
          <span key={t} className="flex items-center gap-1 rounded border border-line px-2 py-0.5 text-xs">
            {t}
            <RoleGate min="site_operator" mode="hide">
              <button
                type="button"
                aria-label={`Remove ${t}`}
                onClick={() => remove(t)}
                className="text-ink-subtle hover:text-critical"
              >
                ×
              </button>
            </RoleGate>
          </span>
        ))}
      </div>
      <RoleGate min="site_operator" mode="hide">
        <div className="mt-2 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="add a tag / group"
            className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-brand"
          />
          <Button variant="secondary" disabled={!input.trim() || setTags.isPending} onClick={add}>
            Add
          </Button>
        </div>
        {ssidChanges && (
          <ApplyGroupSsids serial={serial} changes={ssidChanges} onDone={() => setSsidChanges(null)} />
        )}
      </RoleGate>
    </section>
  );
}

/** The device's display name with inline rename. site_operator+ can edit; read-only sees the
 *  name only. Enter saves, Escape cancels. Renames the dashboard label (Device.hostname). */
function EditableName({ serial, name }: { serial: string; name: string }) {
  const rename = useRenameDevice(serial);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  const save = () => {
    const v = value.trim();
    if (!v || v === name) {
      setEditing(false);
      return;
    }
    rename.mutate(v, { onSuccess: () => setEditing(false) });
  };
  const cancel = () => {
    setValue(name);
    setEditing(false);
  };

  return (
    <RoleGate min="site_operator" mode="hide" fallback={<span className="font-medium text-ink">{name}</span>}>
      {editing ? (
        <span className="flex items-center gap-1.5">
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
            className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 text-sm text-ink outline-none focus:border-brand"
          />
          <Button variant="primary" disabled={rename.isPending} onClick={save}>Save</Button>
          <Button variant="ghost" type="button" onClick={cancel}>Cancel</Button>
        </span>
      ) : (
        <span className="flex items-center gap-2">
          <span className="font-medium text-ink">{name}</span>
          <button
            type="button"
            onClick={() => { setValue(name); setEditing(true); }}
            className="text-xs font-medium text-brand hover:underline"
          >
            Rename
          </button>
          {rename.isError && <span className="text-xs text-critical">Failed.</span>}
        </span>
      )}
    </RoleGate>
  );
}

/**
 * Placement — show the device's current site and move it to another site.
 *
 * Works for ANY device, not just the unassigned pool: moving is a "claim" that sets
 * the device's site (and customer, if the target site is in another customer). This
 * is how you re-home an already-placed device (e.g. move the AP to a new site).
 */
function PlacementSection({
  serial,
  currentSiteId,
  siteName,
  orgId,
}: {
  serial: string;
  currentSiteId: string | null;
  // Backend-resolved site name (to_v2_dict); the source of truth for the CURRENT site so we
  // don't depend on a me.sites lookup that broke on a string(me.sites id) vs number(site_id)
  // mismatch -> "Unassigned" even when placed.
  siteName: string | null;
  orgId: string;
}) {
  const me = useSession((s) => s.me);
  const assign = useAssignDevices();
  const [target, setTarget] = useState("");
  // POST /devices/assign is @require("org_admin") (api/v2/devices.py). A site manager used to
  // see Move and get a silent "skipped"; now they get the reason instead (UI review 2026-10-17).
  const canWrite = useSession((s) => s.hasRole("site_operator"));

  const sites = me?.sites ?? [];
  const orgName = (id: string) => me?.orgs.find((o) => o.id === id)?.name ?? "";
  const multiCustomer = (me?.orgs.length ?? 0) > 1;
  // Exclude the device's current site from "Move to" -- coerce both sides (id types differ).
  const options = sites.filter((s) => String(s.id) !== String(currentSiteId));

  // OP#1935: unchecked by default -- a move changes only the placement unless the operator opts
  // in to the target customer's onboarding default (rename / tags / firmware), listed below.
  const [applyDefault, setApplyDefault] = useState(false);
  const targetSite = sites.find((s) => String(s.id) === String(target));
  // Remember where it went so the result line can name it after the select resets.
  const [movedTo, setMovedTo] = useState("");

  const move = () => {
    if (!target) return;
    setMovedTo(targetSite?.name ?? "");
    assign.mutate(
      {
        serials: [serial],
        site_id: Number(target),
        ...(applyDefault ? { apply_customer_default: true } : {}),
      },
      { onSuccess: () => { setTarget(""); setApplyDefault(false); } },
    );
  };

  return (
    <section>
      <SectionTitle>Placement</SectionTitle>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Current site">
          {siteName
            ? multiCustomer
              ? `${orgName(orgId)} · ${siteName}`
              : siteName
            : "Unassigned"}
        </Field>
      </dl>
      <RoleGate
        min="org_admin"
        mode="hide"
        fallback={
          <p className="mt-2 text-xs text-ink-subtle" data-testid="placement-role-note">
            {canWrite ? "Adding or moving devices between sites requires a customer admin." : "You have read-only access."}
          </p>
        }
      >
        <div className="mt-2 flex items-center gap-2">
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-brand"
          >
            <option value="">Move to…</option>
            {options.map((s) => (
              <option key={s.id} value={s.id}>
                {multiCustomer ? `${orgName(s.org_id)} · ${s.name}` : s.name}
              </option>
            ))}
          </select>
          <Button variant="secondary" disabled={!target || assign.isPending} onClick={move}>
            {assign.isPending ? "Moving…" : "Move"}
          </Button>
        </div>
        {target && (
          <CustomerDefaultOptIn
            orgId={targetSite?.org_id}
            checked={applyDefault}
            onChange={setApplyDefault}
          />
        )}
        {assign.isError && <p className="mt-1 text-xs text-critical">Move failed.</p>}
        {/* What the move ACTUALLY changed (OP#1935): placement, then any rename / tags /
            firmware decision from the server's per-device results and firmware_queued. */}
        {assign.data && (
          <ul
            className={`mt-1 text-xs ${assign.data.assigned > 0 ? "text-healthy" : "text-warn"}`}
            data-testid="move-result"
          >
            {moveResultLines(assign.data, movedTo).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </RoleGate>
    </section>
  );
}

/**
 * Gateway — the read-only wire view of a whitebox uCentral gateway (QOTOM-class x86): a summary
 * strip (WAN/LAN counts, VLANs, DHCP scopes, NAT, captive) and one card per interface with its
 * role, ports, VLAN, IPv4 addressing/subnet, DHCP scope, and flags. Also lists the raw schema
 * sections the config carries — the POC "which sections does this build populate" diagnostic.
 * Renders nothing when owgw has no config. Editing is a later increment.
 */
function GatewaySection({ serial }: { serial: string }) {
  const { data, isLoading, error } = useDeviceGateway(serial);
  if (error)
    return (
      <section>
        <SectionTitle>Gateway</SectionTitle>
        <ErrorBanner error={error} />
      </section>
    );
  if (isLoading || !data || !data.owgw_available) return null;
  const s = data.summary;

  return (
    <section>
      <SectionTitle>Gateway</SectionTitle>
      {/* Summary strip — the posture at a glance. */}
      <div className="mb-3 flex flex-wrap gap-1.5 text-xs">
        <Stat label="WAN" value={s.wan.length ? s.wan.join(", ") : "—"} />
        <Stat label="LAN" value={s.lan.length ? String(s.lan.length) : "0"} />
        <Stat label="VLANs" value={s.vlans.length ? s.vlans.join(", ") : "—"} />
        <Stat label="DHCP scopes" value={String(s.dhcp_scopes)} />
        <Stat label="NAT" value={s.nat ? "yes" : "no"} tone={s.nat ? "healthy" : undefined} />
        <Stat label="Captive" value={s.captive ? "yes" : "no"} tone={s.captive ? "healthy" : undefined} />
      </div>

      {/* No `metrics` section: the warning every Gateway preview carries, plus its one fix
          ("Enable state reporting" -> metrics.ensure, previewed, OP #1678). */}
      <div className="mb-3">
        <StateReportingNotice serial={serial} metricsPresent={s.metrics_present} />
      </div>

      <ul className="flex flex-col gap-2">
        {data.interfaces.map((i) => (
          <li key={`${i.name}-${i.vlan ?? ""}`} className="rounded-md border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-ink">{i.name ?? "—"}</span>
              <span
                className={`rounded px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide ${
                  i.role === "upstream" ? "bg-brand/10 text-brand" : "border border-line text-ink-subtle"
                }`}
              >
                {i.role === "upstream" ? "WAN" : "LAN"}
              </span>
              {i.vlan != null && <Chip>VLAN {i.vlan}</Chip>}
              {i.ports.map((p) => (
                <Chip key={p}>{p}</Chip>
              ))}
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              <Field label="IPv4">
                {i.ipv4.addressing === "static" ? (
                  <Mono>{i.ipv4.subnet ?? "static"}</Mono>
                ) : i.ipv4.addressing === "dynamic" ? (
                  "DHCP client"
                ) : i.ipv4.addressing === "none" ? (
                  <span className="text-ink-muted">L2 bridge (no IP)</span>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="DHCP scope">
                {i.dhcp ? (
                  <span className="tabular-nums">
                    {i.dhcp.lease_count ?? "?"} leases from .{i.dhcp.lease_first ?? "?"}
                    {i.dhcp.lease_time ? ` · ${i.dhcp.lease_time}` : ""}
                  </span>
                ) : (
                  <span className="text-ink-subtle">none</span>
                )}
              </Field>
              {(i.ipv6 || i.captive || i.services.length > 0 || i.ssids.length > 0) && (
                <div className="col-span-2 flex flex-wrap items-center gap-1.5 pt-0.5">
                  {i.ipv6 && <Chip>IPv6</Chip>}
                  {i.captive && (
                    <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
                      captive portal
                    </span>
                  )}
                  {i.ssids.map((n) => (
                    <Chip key={n}>SSID {n}</Chip>
                  ))}
                  {i.services.length > 0 && (
                    <span className="text-ink-subtle">services: {i.services.join(", ")}</span>
                  )}
                </div>
              )}
            </dl>
          </li>
        ))}
      </ul>

      {/* POC diagnostic: which schema sections this build actually carries. */}
      <p className="mt-2 text-[0.6875rem] text-ink-subtle">
        Schema sections: {s.sections.length ? s.sections.join(" · ") : "—"}
        {data.config_uuid != null ? ` · config #${data.config_uuid}` : ""}
      </p>

      {/* Push ONLY the Meridian-managed config-raw lines (no edit), with its own preview and an
          honest audit name — replaces riding them on a no-op edit (2026-10-08). */}
      <div className="mt-3 flex justify-end">
        <ReassertGatewayButton serial={serial} />
      </div>
    </section>
  );
}

/** Label/value pill for the gateway summary strip. */
function Stat({ label, value, tone }: { label: string; value: string; tone?: "healthy" }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-line px-2 py-0.5">
      <span className="uppercase tracking-wide text-ink-subtle">{label}</span>
      <span className={`font-medium tabular-nums ${tone === "healthy" ? "text-healthy" : "text-ink"}`}>{value}</span>
    </span>
  );
}

/** Small mono chip (port names, VLAN ids, flags). */
function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem]">{children}</span>;
}

/**
 * SSIDs — exactly what this AP is broadcasting, read from its live uCentral config (the
 * Meraki/UniFi AP-pane "SSIDs (broadcasting)" view). Each row shows the network name, the bands
 * it rides, its security, VLAN, and the live associated-client count. Renders nothing when owgw
 * has no config for the AP (e.g. just-adopted, not yet pushed). Read-only — SSIDs are a SITE
 * property, edited under Wireless; this is the per-AP truth of what actually went out.
 */
function SsidsSection({ serial }: { serial: string }) {
  const { data, isLoading, error } = useDeviceSsids(serial);
  if (error)
    return (
      <section>
        <SectionTitle>SSIDs</SectionTitle>
        <ErrorBanner error={error} />
      </section>
    );
  if (isLoading || !data || !data.owgw_available || data.ssids.length === 0) return null;

  return (
    <section>
      <SectionTitle>SSIDs broadcasting ({data.ssids.length})</SectionTitle>
      <ul className="flex flex-col gap-1.5">
        {data.ssids.map((s) => (
          <li
            key={s.name}
            className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-ink">{s.name}</span>
                {s.hidden && (
                  <span className="rounded border border-line px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
                    hidden
                  </span>
                )}
                {/* OP#1814: the gateway holds this SSID for the AP, but the AP does not report it
                    on air (e.g. a push reverted by the gateway's reconnect upgrade). */}
                {s.on_air === false && (
                  <span
                    className="rounded border border-critical px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-critical"
                    title="Configured for this AP, but the AP does not report broadcasting it. See the config status."
                  >
                    not on air
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
                <span>{fmtSecurity(s.security)}</span>
                {s.bands.length > 0 && <span aria-hidden>·</span>}
                {s.bands.map((b) => (
                  <span key={b} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem]">
                    {b}
                  </span>
                ))}
                {s.vlan != null && (
                  <>
                    <span aria-hidden>·</span>
                    <span>VLAN {s.vlan}</span>
                  </>
                )}
              </div>
            </div>
            <span className="shrink-0 text-right text-xs text-ink-subtle">
              {s.clients != null ? (
                <>
                  <span className="font-mono text-sm text-ink">{s.clients}</span> client
                  {s.clients === 1 ? "" : "s"}
                </>
              ) : (
                "—"
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Human security label for an SSID: "wpa2-enterprise" -> "WPA2 Enterprise", "open" -> "Open". */
function fmtSecurity(s: string): string {
  if (!s || s === "open") return "Open";
  return s
    .split("-")
    .map((p) => (p.startsWith("wpa") ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(" ");
}

/**
 * Firmware — running version, the managed baseline for this model, and a one-click upgrade.
 *
 * The upgrade is owgw-driven: it queues a FirmwareUpdate and (when the executor + a reachable
 * download host are configured) issues the flash immediately; otherwise it queues as intent and
 * the background executor fires it once configured. The panel polls while an upgrade is in flight
 * (the hook drives the interval) so status/progress advance without a manual refresh. This closes
 * the "APs never have updated firmware" gap: an operator brings any AP to baseline from here.
 */
function FirmwareSection({ serial }: { serial: string }) {
  const { data, isLoading, error } = useDeviceFirmware(serial);
  const upgrade = useUpgradeFirmware(serial);
  // Server floor (UI review 2026-10-17): POST /devices/<serial>/firmware/upgrade is site_operator,
  // BUT an UNAPPROVED image (is_stable=false) is refused with 403 for every caller below
  // platform_admin (services/firmware_manager.check_upgrade_target). The panel used to offer the
  // newest build to everyone, so a site manager / customer admin clicked into a 403. Below
  // platform_admin the target is now the newest APPROVED build; a newer unapproved one is named.
  const canFlashUnapproved = useSession((s) => s.hasRole("platform_admin"));

  if (error)
    return (
      <section>
        <SectionTitle>Firmware</SectionTitle>
        <ErrorBanner error={error} />
      </section>
    );
  if (isLoading || !data) return null;

  const newest = data.latest_available;
  // The image this caller may actually flash (see canFlashUnapproved above).
  const latest =
    canFlashUnapproved || !newest || newest.is_stable ? newest : data.latest_stable;
  // A newer build exists but is not approved, so this caller cannot flash it.
  const unapprovedNewer =
    !canFlashUnapproved && newest && !newest.is_stable && newest.id !== latest?.id ? newest : null;
  const update = data.latest_update;
  const inFlight =
    update != null && ["pending", "downloading", "applying"].includes(update.status);
  // Up to date = the running string carries the baseline version (owgw reports a long string,
  // the image version is the short tag) -- same substring rule the backend reconcile uses.
  const upToDate =
    !!latest &&
    !!data.current_version &&
    data.current_version.toLowerCase().includes(latest.version.toLowerCase());

  // Why the upgrade control is unavailable, if it is (drives the disabled explainer).
  const blockedReason = !latest
    ? unapprovedNewer
      ? `v${unapprovedNewer.version} is not approved yet. Only approved images can be flashed; a platform admin approves builds.`
      : `No firmware image is registered for ${data.model ?? "this model"} yet.`
    : upToDate
      ? `Already on the baseline (v${latest.version}).`
      : inFlight
        ? "An upgrade is already in progress."
        : null;

  const doUpgrade = () => {
    if (!latest) return;
    upgrade.mutate({ firmware_id: latest.id });
  };

  return (
    <section>
      <SectionTitle>Firmware</SectionTitle>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Running">
          <Mono>{data.current_version ?? "—"}</Mono>
        </Field>
        <Field label="Baseline available">
          {latest ? (
            <span className="flex items-center gap-1.5">
              <Mono>{latest.version}</Mono>
              {latest.is_stable && (
                <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
                  stable
                </span>
              )}
            </span>
          ) : (
            "—"
          )}
        </Field>
      </dl>

      {unapprovedNewer && latest && (
        <p className="mt-2 text-xs text-ink-subtle" data-testid="fw-unapproved-note">
          Newer build v{unapprovedNewer.version} is not approved yet, so it is not offered here.
        </p>
      )}

      {upToDate && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-healthy">
          <span aria-hidden>✓</span> Up to date with the managed baseline.
        </p>
      )}

      {/* Live status of the most recent update (in-flight or a terminal failure worth showing). */}
      {update && (inFlight || update.status === "failed") && (
        <div className="mt-3 rounded-md border border-line bg-surface-2 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm">
              <FirmwareStatusPill status={update.status} />
              {update.firmware_version && (
                <span className="text-ink-muted">
                  → v<Mono>{update.firmware_version}</Mono>
                </span>
              )}
            </span>
            {inFlight && (
              <span className="font-mono text-xs text-ink-subtle">{update.progress}%</span>
            )}
          </div>
          {inFlight && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-line">
              <div
                className="h-full rounded-full bg-brand transition-all"
                style={{ width: `${Math.max(4, update.progress)}%` }}
              />
            </div>
          )}
          {update.status === "failed" && update.error_message && (
            <p className="mt-1.5 text-xs text-critical">{update.error_message}</p>
          )}
        </div>
      )}

      {/* Upgrade control — gated. read-only never sees it; a role that could-but-can't at this
          scope sees it disabled with the reason. */}
      <RoleGate min="site_operator" mode="hide">
        <div className="mt-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={!!blockedReason || upgrade.isPending}
              title={blockedReason ?? undefined}
              onClick={doUpgrade}
            >
              {upgrade.isPending ? "Starting…" : "Upgrade to baseline"}
            </Button>
            {blockedReason && !inFlight && (
              <span className="text-xs text-ink-subtle">{blockedReason}</span>
            )}
          </div>
          {/* Tell the operator whether hitting the button flashes now or just records intent. */}
          {latest && !upToDate && !inFlight && (
            <p className="text-xs text-ink-subtle">
              {data.executor_enabled && data.download_host_configured
                ? "The AP will download and flash, then reconnect on the new firmware."
                : "Queues the upgrade — it runs automatically once the firmware host is configured."}
            </p>
          )}
          {upgrade.isError && <span className="text-xs text-critical">Could not start the upgrade.</span>}
          {upgrade.data && !upgrade.data.issued && upgrade.isSuccess && (
            <span className="text-xs text-warn">Queued as intent (not issued yet).</span>
          )}
        </div>
      </RoleGate>
    </section>
  );
}

/** Status pill for a firmware update, colored by state (in-flight = brand, done = healthy,
 *  failed = critical). Mirrors the tone of DeviceStatusPill without importing its device map. */
function FirmwareStatusPill({ status }: { status: string }) {
  const tone =
    status === "completed"
      ? "bg-healthy/10 text-healthy"
      : status === "failed"
        ? "bg-critical/10 text-critical"
        : "bg-brand/10 text-brand";
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide ${tone}`}
    >
      {status}
    </span>
  );
}

/**
 * Configuration — the "apply config from the UI" panel.
 *
 * Lists the curated config actions for this device (GET .../config/actions) and,
 * for each, drives the preview -> confirm -> apply ritual. The apply is the
 * accountable write (controller -> owgw -> device, audited); read-only users see
 * the actions but not the apply control.
 */
function ConfigurationSection({ serial, kind }: { serial: string; kind?: string }) {
  const { data, isLoading, error } = useDeviceConfigActions(serial);
  const navigate = useNavigate();
  // The port table is a SCOPED section (/o/:org[/s/:site]/switch-ports); the old absolute
  // "/switch-ports" matched no route. Deep-link with this switch filtered.
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);

  return (
    <section>
      <SectionTitle>Configuration</SectionTitle>
      {/* OP#1783: field-level drift (intended vs reported) leads the section, read-only. */}
      <ConfigDriftPanel serial={serial} />
      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading actions…</div>}
      {data && data.owgw_available === false && kind !== "switch" && (
        <p className="text-sm text-ink-muted">
          Live configuration is not available for this device from the gateway.
        </p>
      )}
      {/* A switch's ports are configured over ecCLI/SNMP, which has nothing to do with
          whether the GATEWAY can also reach the device. Gating this on `owgw_available ===
          false` tied together two unrelated facts, and the consequence was real: adopting the
          ECS2100 into uCentral flipped owgw_available to true and silently removed the only
          route to port configuration from this screen. The Ports section above is now the
          primary path; this stays as the way into the advanced editor. */}
      {kind === "switch" && (
        <button
          type="button"
          onClick={() => navigate(portTableHref({ orgId, siteId, serial }))}
          className="text-sm font-medium text-brand hover:underline"
        >
          Open in the port table →
        </button>
      )}
      {data && data.owgw_available && (
        <ul className="flex flex-col gap-2">
          {data.actions.map((action) => (
            <ConfigActionRow key={action.id} serial={serial} action={action} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** One config action: label + description, a scope chip, an applied badge, and
 *  the preview -> confirm -> apply flow driven by local state. */
function ConfigActionRow({ serial, action }: { serial: string; action: DeviceConfigAction }) {
  const preview = usePreviewConfigAction(serial);
  const apply = useApplyConfigAction(serial);
  // idle -> confirm (preview loaded) ; the mutations carry their own pending/error.
  const [confirming, setConfirming] = useState(false);

  const previewData = preview.data;
  const applied = action.applied || (apply.data?.applied ?? false);
  const disabled = !action.supported;

  const startPreview = () => {
    setConfirming(true);
    preview.mutate(action.id);
  };
  const cancel = () => {
    setConfirming(false);
    preview.reset();
  };
  const confirmApply = () => {
    apply.mutate(action.id, {
      onSettled: () => setConfirming(false),
    });
  };

  return (
    <li className="rounded-md border border-line p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">{action.label}</span>
            <ScopeChip scope={action.scope} />
            {applied && (
              <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
                Applied
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-ink-muted">{action.description}</p>
        </div>

        {/* Apply is org_admin (OP#1580; server: POST /devices/<serial>/config/actions/<id>/apply).
            Below that the action is visible, the button is not. */}
        {!applied && (
          <RoleGate
            min="org_admin"
            mode="hide"
            fallback={<span className="shrink-0 text-xs text-ink-subtle">read-only</span>}
          >
            {!confirming && (
              <Button
                variant="secondary"
                disabled={disabled}
                title={disabled ? "Not applicable to this device" : undefined}
                onClick={startPreview}
              >
                Enable
              </Button>
            )}
          </RoleGate>
        )}
      </div>

      {/* Preview / confirm strip. */}
      {confirming && (
        <div className="mt-3 rounded border border-line bg-surface-2 p-2 text-xs">
          {preview.isPending && <span className="text-ink-muted">Previewing…</span>}
          {preview.isError && <span className="text-critical">Preview failed.</span>}
          {previewData && !previewData.changed && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-muted">Already applied — no change.</span>
              <Button variant="ghost" onClick={cancel}>
                Close
              </Button>
            </div>
          )}
          {previewData && previewData.changed && (
            <div className="flex flex-col gap-2">
              <div>
                <span className="text-ink-muted">This will change: </span>
                <span className="font-mono text-ink">
                  {previewData.diff.map((d) => d.path).join(", ") || "configuration"}
                </span>
                <span className="text-ink-muted"> — pushed to the device via the gateway.</span>
              </div>
              {apply.isError && <span className="text-critical">Apply failed.</span>}
              <div className="flex items-center gap-2">
                <Button variant="primary" disabled={apply.isPending} onClick={confirmApply}>
                  {apply.isPending ? "Applying…" : "Confirm & apply"}
                </Button>
                <Button variant="ghost" disabled={apply.isPending} onClick={cancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Small chip distinguishing a device-only action from a site-wide one. */
function ScopeChip({ scope }: { scope: "device" | "site" }) {
  return (
    <span className="rounded border border-line px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
      {scope === "site" ? "Site-wide" : "This device"}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

/** Small uppercase section heading. */
function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
      {children}
    </h3>
  );
}

/** Plain-language role of one reported address ("LAN", "Uplink", "VLAN 41", ...). */
function addressRole(a: DeviceAddress): string {
  switch (a.role) {
    case "upstream":
      return "Uplink";
    case "downstream":
      return a.vlan ? `LAN · VLAN ${a.vlan}` : "LAN";
    case "svi":
      return a.vlan != null ? `VLAN ${a.vlan}` : "VLAN";
    case "dhcp_lease":
      return "DHCP lease";
    default:
      return "Interface";
  }
}

/**
 * Every address the device reported on its own interfaces (or the gateway lease it was
 * learned from). Hidden when there is nothing beyond the single Private IP above -- a row
 * that repeats one value adds nothing. Shown for a gateway with several LAN VLANs, an AP
 * that also serves its own NAT subnet, or a switch with several SVIs.
 */
function InterfaceAddresses({ device }: { device: Device }) {
  const addrs = device.private_addresses ?? [];
  const first = addrs[0];
  const lease = addrs.length === 1 && first?.role === "dhcp_lease" ? first : null;
  if (lease) {
    // Learned from the gateway, not the device: say so, since it can lag a re-lease.
    return (
      <div className="col-span-2 -mt-1 text-xs text-ink-muted">
        Private IP learned from the DHCP lease on {lease.iface ?? "its gateway"}; this device
        reports no interface addresses of its own.
      </div>
    );
  }
  if (addrs.length < 2) return null;
  return (
    <div className="col-span-2">
      <Field label="Interface addresses">
        <ul className="flex flex-col gap-0.5">
          {addrs.map((a) => (
            <li key={`${a.iface}-${a.ip}`} className="flex items-baseline gap-2">
              <Mono>
                {a.ip}
                {a.prefix != null ? `/${a.prefix}` : ""}
              </Mono>
              <span className="text-xs text-ink-muted">
                {addressRole(a)}
                {a.iface ? ` · ${a.iface}` : ""}
                {a.ip === device.private_ip ? " · primary" : ""}
              </span>
            </li>
          ))}
        </ul>
      </Field>
    </div>
  );
}

/** Monospace for identifiers (MAC, serial, IP, port ids) so they line up and scan. */
function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[0.8125rem]">{children}</span>;
}

/** Human link speed, e.g. "2.5 Gbps · full". "Link down" when the port has no
 *  negotiated speed (offline / no cable). */
function fmtSpeed(u?: DeviceUplink): string {
  if (!u || u.speed_mbps == null) return "Link down";
  const s = u.speed_mbps >= 1000 ? `${u.speed_mbps / 1000} Gbps` : `${u.speed_mbps} Mbps`;
  return u.duplex ? `${s} · ${u.duplex}` : s;
}

/** Compact per-port table for switches: port · status · speed · VLAN · PoE · neighbor. */
function PortsTable({ ports }: { ports: SwitchPort[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-ink-subtle">
          <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
            <th>Port</th>
            <th>Status</th>
            <th>Speed</th>
            <th>VLAN</th>
            <th>PoE</th>
            <th>Neighbor (LLDP)</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {ports.map((p) => (
            <tr key={p.name} className="border-t border-line/60 [&>td]:py-1 [&>td]:pr-3">
              <td title={p.name}>{formatSwitchPort(p.name)}</td>
              <td>
                <span
                  className={
                    p.status === "up"
                      ? "text-healthy"
                      : p.status === "disabled"
                        ? "text-ink-subtle"
                        : "text-ink-muted"
                  }
                >
                  {p.status}
                </span>
              </td>
              <td>{p.status === "up" && p.speed_mbps ? fmtPortSpeed(p.speed_mbps) : "—"}</td>
              <td>{p.vlan ?? "—"}</td>
              <td>{fmtPoe(p.poe)}</td>
              <td className="pr-0">
                {p.lldp?.system_name
                  ? `${p.lldp.system_name}${p.lldp.port_id ? ` · ${formatPortRefs(p.lldp.port_id)}` : ""}`
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Compact port speed: "2.5G", "1G", "100M". */
function fmtPortSpeed(mbps: number): string {
  return mbps >= 1000 ? `${mbps / 1000}G` : `${mbps}M`;
}

/** PoE cell: delivered watts, "on", or "—" when off/non-PoE. */
function fmtPoe(poe?: SwitchPort["poe"]): string {
  if (!poe || !poe.enabled) return "—";
  return poe.powered_w != null ? `${poe.powered_w}W` : "on";
}
